/* eslint-disable no-undef */
import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { ChevronRightOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  MENU_ITEMS_ADMIN,
  MENU_ITEMS_APP,
  MENU_ITEMS_AGENT,
} from "../constants/Constants";
import { SLATE } from "../constants/Colors";
import { usePageMeta } from "../contexts/PageMetaContext";

// ─── Route info helper ────────────────────────────────────────────────────────
/**
 * Returns { breadcrumbs: [{label, path}], title } from the current pathname.
 * Each crumb with a non-null path is rendered as a clickable link.
 *
 *  /clients, /           → breadcrumbs: [],                          title: "Clients"
 *  /admin/agent-templates → breadcrumbs: [{label:"Admin", path:null}], title: "Agent Types"
 *  /:app/                → breadcrumbs: [{label:"Clients", path:"/"}], title: "Agents"
 *  /:app/agent/:id/prof  → breadcrumbs: [{label:"Clients",path:"/"},{label:appName,path:"/:app/"}], title: "Profile"
 */
function getRouteInfo(pathname, currentApp) {
  const segments = pathname.split("/").filter(Boolean);

  // ── Admin / no-app context ────────────────────────────────────────────────
  if (!currentApp || !segments[0] || segments[0] !== currentApp.url_code) {
    if (segments[0] === "admin") {
      // ── Admin sub-routes (create / edit pages) ────────────────────────────
      if (pathname === "/admin/capabilities/new") {
        return {
          breadcrumbs: [{ label: "Capabilities Store", path: "/admin/capabilities" }],
          title: "Create Capability",
        };
      }
      if (/^\/admin\/capabilities\/[^/]+\/edit$/.test(pathname)) {
        return {
          breadcrumbs: [{ label: "Capabilities Store", path: "/admin/capabilities" }],
          title: "Edit Capability",
        };
      }
      if (pathname === "/admin/prompt-variable-templates/new") {
        return {
          breadcrumbs: [{ label: "Agent Variables", path: "/admin/agent-variables" }],
          title: "Create Template",
        };
      }
      if (/^\/admin\/prompt-variable-templates\/[^/]+\/edit$/.test(pathname)) {
        return {
          breadcrumbs: [{ label: "Agent Variables", path: "/admin/agent-variables" }],
          title: "Edit Template",
        };
      }
      if (pathname === "/admin/agent-templates/new") {
        return {
          breadcrumbs: [{ label: "Agent Store", path: "/admin/agent-templates" }],
          title: "Create Agent Template",
        };
      }
      if (/^\/admin\/agent-templates\/[^/]+/.test(pathname)) {
        return {
          breadcrumbs: [{ label: "Agent Store", path: "/admin/agent-templates" }],
          title: "Edit Agent Template",
        };
      }
      // ── General MENU_ITEMS_ADMIN matching ─────────────────────────────────
      // Top-level items get no parent breadcrumb; group children use the group title
      for (const item of MENU_ITEMS_ADMIN) {
        if (item.path && pathname.startsWith(item.path)) {
          return { breadcrumbs: [], title: item.title };
        }
        if (item.children) {
          for (const child of item.children) {
            if (child.path && pathname.startsWith(child.path)) {
              return {
                breadcrumbs: [{ label: item.title, path: null }],
                title: child.title,
              };
            }
          }
        }
      }
      return { breadcrumbs: [], title: "" };
    }

    // Create client
    if (pathname === "/clients/new") {
      return {
        breadcrumbs: [
          { label: "Clients", path: "/" },
          { label: "Create Client", path: null },
        ],
        title: "Create Client",
      };
    }

    // Import clients
    if (pathname === "/clients/import") {
      return {
        breadcrumbs: [
          { label: "Clients", path: "/" },
          { label: "Import Clients", path: null },
        ],
        title: "Import Clients",
      };
    }

    for (const item of MENU_ITEMS_ADMIN) {
      const paths = [item.path, item.alternatePath].filter(Boolean);
      for (const p of paths) {
        if (pathname === p || pathname.startsWith(p + "/")) {
          return { breadcrumbs: [], title: item.title };
        }
      }
    }
    return { breadcrumbs: [], title: "" };
  }

  // ── App context ───────────────────────────────────────────────────────────
  const appName = currentApp.client_name;
  const appUrlCode = currentApp.url_code;
  const appSegs = segments.slice(1); // drop /:app_name

  // Agent detail: /:app_name/agent/:id/...
  if (appSegs[0] === "agent" && appSegs[1] && !isNaN(appSegs[1])) {
    const agentSubPath = "/" + appSegs.slice(2).join("/");

    if (!agentSubPath || agentSubPath === "/") {
      return {
        breadcrumbs: [
          { label: "Clients", path: "/" },
          { label: appName, path: `/${appUrlCode}/` },
        ],
        title: "Profile",
      };
    }

    // Capability configure sub-routes: /capabilities/new or /capabilities/:id
    if (/^\/capabilities\/.+$/.test(agentSubPath)) {
      const agentId = appSegs[1];
      return {
        breadcrumbs: [
          { label: "Clients", path: "/" },
          { label: appName, path: `/${appUrlCode}/` },
          { label: "Capabilities", path: `/${appUrlCode}/agent/${agentId}/capabilities` },
        ],
        title: "Configure Capability",
      };
    }

    for (const item of MENU_ITEMS_AGENT) {
      if (
        item.path &&
        (agentSubPath === item.path || agentSubPath.startsWith(item.path + "/"))
      ) {
        return {
          breadcrumbs: [
            { label: "Clients", path: "/" },
            { label: appName, path: `/${appUrlCode}/` },
          ],
          title: item.title,
        };
      }
      if (item.children) {
        for (const child of item.children) {
          if (
            child.path &&
            (agentSubPath === child.path ||
              agentSubPath.startsWith(child.path + "/"))
          ) {
            return {
              breadcrumbs: [
                { label: "Clients", path: "/" },
                { label: appName, path: `/${appUrlCode}/` },
              ],
              title: child.title,
            };
          }
        }
      }
    }
    return {
      breadcrumbs: [
        { label: "Clients", path: "/" },
        { label: appName, path: `/${appUrlCode}/` },
      ],
      title: "Agent",
    };
  }

  // Create agent
  if (appSegs[0] === "agents" && appSegs[1] === "new") {
    return {
      breadcrumbs: [
        { label: "Clients", path: "/" },
        { label: appName, path: `/${appUrlCode}/` },
      ],
      title: "Create Agent",
    };
  }

  // Edit client
  if (appSegs[0] === "manage") {
    return {
      breadcrumbs: [
        { label: "Clients", path: "/" },
        { label: appName, path: null },
      ],
      title: "Edit Client",
    };
  }

  // App root (agents list)
  if (appSegs.length === 0) {
    return {
      breadcrumbs: [
        { label: "Clients", path: "/" },
        { label: appName, path: `/${appUrlCode}/` },
      ],
      title: "Agents",
    };
  }

  // Business unit sub-routes — add "Business Units" as a clickable crumb
  if (appSegs[0] === "business-units" && appSegs[1]) {
    const buListPath = `/${appUrlCode}/business-units`;
    if (appSegs[1] === "new") {
      return {
        breadcrumbs: [
          { label: "Clients", path: "/" },
          { label: appName, path: `/${appUrlCode}/` },
          { label: "Business Units", path: buListPath },
        ],
        title: "Create Business Unit",
      };
    }
    if (appSegs[2] === "edit") {
      return {
        breadcrumbs: [
          { label: "Clients", path: "/" },
          { label: appName, path: `/${appUrlCode}/` },
          { label: "Business Units", path: buListPath },
        ],
        title: "Edit Business Unit",
      };
    }
    if (appSegs[2] === "agents") {
      return {
        breadcrumbs: [
          { label: "Clients", path: "/" },
          { label: appName, path: `/${appUrlCode}/` },
          { label: "Business Units", path: buListPath },
        ],
        title: "Agents",
      };
    }
  }

  // Other app sub-pages
  const appPath = "/" + appSegs.join("/");
  for (const item of MENU_ITEMS_APP) {
    if (
      item.path &&
      (appPath === item.path || appPath.startsWith(item.path + "/"))
    ) {
      return {
        breadcrumbs: [
          { label: "Clients", path: "/" },
          { label: appName, path: `/${appUrlCode}/` },
        ],
        title: item.title,
      };
    }
  }

  const lastSeg = appSegs[appSegs.length - 1];
  const label = lastSeg
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return {
    breadcrumbs: [
      { label: "Clients", path: "/" },
      { label: appName, path: `/${appUrlCode}/` },
    ],
    title: label,
  };
}

// ─── TopBar ───────────────────────────────────────────────────────────────────
export default function TopBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const selectedAgentName = useSelector((state) => state?.apps?.selectedAgentName);
  const { pageMeta } = usePageMeta();

  const { breadcrumbs: routeBreadcrumbs, title } = useMemo(
    () => getRouteInfo(location.pathname, currentApp),
    [location.pathname, currentApp]
  );

  const isAgentRoute = /^\/[^/]+\/agent\/[^/]+/.test(location.pathname);
  const agentLabel = pageMeta.agentName || (isAgentRoute ? selectedAgentName : null);

  const breadcrumbs = agentLabel
    ? [...routeBreadcrumbs, { label: agentLabel, path: null }]
    : pageMeta.itemName
    ? [...routeBreadcrumbs, { label: pageMeta.itemName, path: null }]
    : routeBreadcrumbs;

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        width: "100%",
        minHeight: 80,
        zIndex: 1100,
        bgcolor: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(0,0,0,0.07)",
        boxShadow: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: "12px",
        flexShrink: 0,
        gap: 2,
      }}
    >
      {/* ── Left: breadcrumb + title + optional description ─────────────── */}
      <Box sx={{ minWidth: 0 }}>
        {/* Clickable breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "2px", mb: "3px" }}>
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && (
                  <ChevronRightOutlined sx={{ fontSize: "0.875rem", color: SLATE[300], flexShrink: 0 }} />
                )}
                {crumb.path ? (
                  <Typography
                    onClick={() => navigate(crumb.path)}
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: SLATE[400],
                      lineHeight: 1.4,
                      cursor: "pointer",
                      borderRadius: "4px",
                      px: "4px",
                      py: "1px",
                      transition: "color 0.15s, background 0.15s",
                      "&:hover": { color: SLATE[800], bgcolor: SLATE[100] },
                    }}
                  >
                    {crumb.label}
                  </Typography>
                ) : (
                  <Typography sx={{
                    fontSize: "0.75rem",
                    fontWeight: idx === breadcrumbs.length - 1 ? 600 : 500,
                    color: idx === breadcrumbs.length - 1 ? SLATE[900] : SLATE[500],
                    lineHeight: 1.4,
                    px: "4px",
                  }}>
                    {crumb.label}
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}

        {title && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: 600,
                color: SLATE[900],
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
            {pageMeta.badge && (() => {
              const MODE_BADGE_CONFIG = {
                legacy: { label: 'Legacy',   color: '#64748b', bg: '#f1f5f9' },
                agent:  { label: 'AI Agent', color: '#16a34a', bg: '#f0fdf4' },
                hybrid: { label: 'Hybrid',   color: '#2563eb', bg: '#eff6ff' },
              };
              const cfg = MODE_BADGE_CONFIG[pageMeta.badge];
              if (!cfg) return null;
              return (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', px: '8px', py: '3px', borderRadius: '100px', bgcolor: cfg.bg }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cfg.color }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: cfg.color }}>{cfg.label}</Typography>
                </Box>
              );
            })()}
          </Box>
        )}
        {pageMeta.description && (
          <Typography
            sx={{ fontSize: "0.6875rem", color: SLATE[500], lineHeight: 1.4, mt: "2px" }}
          >
            {pageMeta.description}
          </Typography>
        )}
      </Box>

      {/* ── Right: page-level actions ────────────────────────────────────── */}
      {pageMeta.actions && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {pageMeta.actions}
        </Box>
      )}
    </Box>
  );
}
